/** @jsx jsx */
import React, { Fragment, useEffect, useRef, useState } from 'react'
import { css, jsx } from '@emotion/core'
import { observer } from 'mobx-react'
import _ from 'lodash'
import { EMPTY_ICON, MATCHUP_DETAIL_CONTENT_TAB_KEY } from '@constants/index'
import { AdvEmpty } from '@components/AdvEmpty'
import { ListView } from 'antd-mobile'
import { FROM_VIEW_TYPE } from '@v2/constants/common'
import { AdvComments } from '@components/AdvComment/index.style'
import { AdvEditor, AdvEditorData } from '@components/AdvEditor'
import useRootStore from '@store/useRootStore'
import { SimpleMatchTrack } from '@store/matchupStore'
import { CHEER_FOR_TEAM_CONTENTS_TYPE } from '@v2/constants/cheer'
import * as ImageUtils from '@utils/image-utils'
import { SportTalkItem } from 'components/SportTalkItem'
import { AdvTimelineItem } from '@components/SportTalkItem/AdvTimelineItem'
import PageResetIco from '@shared/Icon/EtcIcon/PageResetIco'
import { isNotStartedMatchStatus } from '@utils/utils'
import { AdvButton } from '@components/Button'
import { SportIssueList } from '@views/Matchup/MatchUpDetail/TabContent/SportTalkContents/SportIssueList'
import { Schema$SportTalkListItem } from '@interface/cheer-for-team'
import { SPORT_TALK_ITEM_TYPE } from '@v2/constants/sport-issue'

const ListViewStyle = css`
    position: static !important;

    .am-list-body {
        margin-bottom: 90px;
    }
`

interface SportTalkProps {
  track: SimpleMatchTrack
  sportTalkItems: Schema$SportTalkListItem[]
  hasMoreSportTalk: boolean
  fetchSportTalkHandler: (matchId: number, reset?: boolean, timelineDisabled?: boolean) => Promise<void>
  fromViewType?: FROM_VIEW_TYPE
  contentWrapRef?: React.MutableRefObject<HTMLDivElement>
  isInitBettingShare?: boolean
}

let saveCheerForTeamTimer = null
let refreshSportTalkTimer = null

export const SportTalk: React.FC<SportTalkProps> = observer((props) => {
  const {
    track,
    sportTalkItems,
    hasMoreSportTalk,
    fetchSportTalkHandler,
    fromViewType = FROM_VIEW_TYPE.MATCHUP_DETAIL_CHEER_FOR_TEAM,
    contentWrapRef,
    isInitBettingShare = false
  } = props
  const {
    cheerForTeamStore,
    componentStore,
    matchupStore,
    globalStore,
    chattingStore,
    authStore,
  } = useRootStore()
  const { matchId } = track || ({} as SimpleMatchTrack)
  const [sportIssuePreviewList, setSportIssuePreviewList] = useState([])
  const [sportTalkPickUserInfos, setSportTalkPickUserInfos] = useState([])
  const [editorData, setEditorData] = useState({
    isNew: true,
    content: '',
    attachment: null,
    attachmentType: CHEER_FOR_TEAM_CONTENTS_TYPE.TEXT_ONLY,
    attachmentDetail: null,
    cheerForTeamData: null,
  })
  const isVisibleDashboard = _.size(sportIssuePreviewList) > 0 || _.size(sportTalkPickUserInfos) > 0

  const matchTrackRef = useRef(track)
  const throttleListScrollHandlerRef = useRef<ReturnType<typeof _.throttle>>(null)
  const hasMoreSportTalkRef = useRef(hasMoreSportTalk)
  const isFetchSportTalkLoadingRef = useRef(false)

  const editorIsFocus = isInitBettingShare

  const listScrollHandler = () => {
    if (!hasMoreSportTalkRef.current || isFetchSportTalkLoadingRef.current) {
      return
    }

    if (!contentWrapRef.current) {
      return
    }

    const wrapper = contentWrapRef.current

    // 전체 높이 (scrollHeight) - 화면 높이 (clientHeight) - 마지막 화면에 스크롤 지점 (화면 높이/2) < 현재 스크롤 위치
    // 전체 높이 - 화면 높이 = 최대 scrollTop 에 근접
    // 전체 높이 - 화면 높이 - (화면 높이/2) 는 스크롤이 거의 모두 내려가서 현재 화면 높이의 반 보다 적게 스크롤 할 양이 남았을 경우를 의미한다.
    if (wrapper.scrollHeight - wrapper.clientHeight - (wrapper.clientHeight / 2)
      < wrapper.scrollTop) {
      endReachedHandler()
    }
  }

  // 스크롤 이벤트 해제 시 dom 이 이미 제거되었을 수 있으므로 useOnUnmount 보다 componentWillUnmount 과 유사한 useEffect return 을 이용한다.
  useEffect(() => {
    // 빠르게 여러번 발생하는 이벤트는 스로틀로 0.2초에 한번만 처리한다.
    throttleListScrollHandlerRef.current = _.throttle(listScrollHandler, 200)

    if (contentWrapRef?.current) {
      contentWrapRef.current.addEventListener(
        'scroll',
        throttleListScrollHandlerRef.current,
        true)
    }

    return () => {
      if (contentWrapRef?.current) {
        contentWrapRef.current.removeEventListener('scroll', throttleListScrollHandlerRef.current, true)
      }

      if (throttleListScrollHandlerRef.current) {
        throttleListScrollHandlerRef.current.cancel()
        throttleListScrollHandlerRef.current = null
      }
    }
  }, [contentWrapRef.current])

  useEffect(() => {
    hasMoreSportTalkRef.current = hasMoreSportTalk
  }, [hasMoreSportTalk])

  useEffect(() => {
    matchTrackRef.current = track
  }, [track])

  useEffect(() => {
    if (_.isNil(matchId)) {
      return
    }

    initEditorData()
    fetchDashboard(matchId).catch(console.error)

    isFetchSportTalkLoadingRef.current = false
    fetchSportTalks(matchId, true).catch(console.error)
  }, [matchId])

  const endReachedHandler = async () => {
    const currentMatchId = matchTrackRef.current.matchId
    fetchSportTalks(currentMatchId).catch(console.error)
  }

  const resetEditorData = () => {
    setEditorData({
      isNew: true,
      content: '',
      attachment: null,
      attachmentType: CHEER_FOR_TEAM_CONTENTS_TYPE.TEXT_ONLY,
      attachmentDetail: null,
      cheerForTeamData: null,
    })
  }

  const initEditorData = () => {
    resetEditorData()

    if (isInitBettingShare && authStore.isSignedIn) {
      makeCheerForTeamData().catch(console.error)
    }
  }

  const dataSource = new ListView.DataSource({
    rowHasChanged: (row1, row2) => row1 !== row2,
  })

  const fetchSportTalks = async (matchId: number, reset = false) => {
    if (!fetchSportTalkHandler) {
      return
    }

    if (isFetchSportTalkLoadingRef.current) {
      return
    }

    isFetchSportTalkLoadingRef.current = true
    await fetchSportTalkHandler(matchId, reset)
    isFetchSportTalkLoadingRef.current = false
  }

  const fetchDashboard = async (matchId: number) => {
    const currentSportId = matchTrackRef.current.sportId
    const result = await componentStore.fetchSportDashboardList({ sportId: currentSportId , matchId})

    setSportIssuePreviewList(result?.issues || [])
    setSportTalkPickUserInfos(result?.pickWriterInfos || [])
  }

  const onSave = async (pickEditorData: AdvEditorData): Promise<boolean> => {
    const effectEnabled = isNotStartedMatchStatus(pickEditorData.cheerForTeamData.matchStatus)
      ? false : cheerForTeamStore.effectEnabled
    clearTimeout(saveCheerForTeamTimer)
    saveCheerForTeamTimer = setTimeout(async () => {
      const currentMatchId = matchTrackRef.current.matchId

      const params = {
        contents: pickEditorData.content,
        attachmentType: pickEditorData.attachmentType as CHEER_FOR_TEAM_CONTENTS_TYPE,
        attachment: pickEditorData.attachment,
        attachmentDetail: pickEditorData.attachmentDetail,
        cheerForTeamData: pickEditorData.cheerForTeamData,
        effectEnabled,
      }

      const result = await cheerForTeamStore.api.cheerForTeam.createCheerForTeam(params)
      matchupStore.updateMatchCheerForTeamWritten(pickEditorData.cheerForTeamData.matchId)
      fetchSportTalks(currentMatchId, true).then(() => {
        globalStore.addActivityScoreNoticeQueue(result.activityInfo)

        const { detailMatchId, matchupDetailContentTabKey } = matchupStore

        if (fromViewType !== FROM_VIEW_TYPE.MATCHUP_DETAIL_CHEER_FOR_TEAM
          && currentMatchId === detailMatchId) {
          if (matchupDetailContentTabKey === MATCHUP_DETAIL_CONTENT_TAB_KEY.SPORT_TALK) {
            const timelineEnabled = globalStore.getSportTalkMatchTimelineEnabled()
            cheerForTeamStore.fetchSportTalkListInMatchDetail(
              currentMatchId, true, !timelineEnabled,
            ).catch(console.error)
          }
        }
      }).catch(console.error)
    }, 500)
    return true
  }

  const makeCheerForTeamData = async (): Promise<void> => {
    const currentMatchId = matchTrackRef.current.matchId
    const refreshedCheerForTeamData = await chattingStore.getCheerForTeamMatchInfo(currentMatchId)
    const defaultEffectIcon = ImageUtils.getDefaultCheerForTeamEffectIcon()
    refreshedCheerForTeamData.effectIconUrl = defaultEffectIcon.img
    editorData['cheerForTeamData'] = refreshedCheerForTeamData
    setEditorData(_.cloneDeep(editorData))
  }

  let beforePlaceHolder
  if (!authStore.isSignedIn) {
    beforePlaceHolder = '로그인 후 이용할 수 있습니다.'
  } else {
    beforePlaceHolder = '스포츠톡을 입력해보세요.'
  }

  const onOpen = async (): Promise<void> => {
    if (authStore.isSignedIn) {
      makeCheerForTeamData().catch(console.error)
    }
  }

  const onClickRefreshSportTalkList = () => {
    clearTimeout(refreshSportTalkTimer)
    refreshSportTalkTimer = setTimeout(async () => {
      const currentMatchId = matchTrackRef.current.matchId
      fetchSportTalks(currentMatchId, true).catch(console.error)
      if (contentWrapRef?.current) {
        contentWrapRef.current.scrollTop = 0
      }
    }, 500)
  }

  const renderHeader = (): React.ReactElement => {
    const showCheerForTeam = !_.isNil(editorData.cheerForTeamData?.matchId)
      && !isNotStartedMatchStatus(editorData.cheerForTeamData?.matchStatus)

    let editorFromViewType = FROM_VIEW_TYPE.MATCHUP_DETAIL_CHEER_FOR_TEAM

    if (fromViewType !== FROM_VIEW_TYPE.MATCHUP_DETAIL_CHEER_FOR_TEAM) {
      editorFromViewType = FROM_VIEW_TYPE.SPORT_TALK
    }

    return (
      <Fragment>
        {
          isVisibleDashboard &&
          <SportIssueList
            track={track}
            matchId={track.matchId}
            issuePreviewList={sportIssuePreviewList}
            pickUserInfos={sportTalkPickUserInfos}
          />
        }
        <AdvComments className="sport-talk-editor">
          <div className="sport-talk-tit-wrap">
            <p className="sport-talk-tit-txt">스포츠톡</p>
            <div className="sport-talk-refresh-btn">
              <AdvButton
                flex
                alignCenter
                width={45}
                height={45}
                borderRadius={'50%'}
                ico={<PageResetIco/>}
                className="write-btn"
                onClick={onClickRefreshSportTalkList}
              />
            </div>
          </div>
          <AdvEditor
            showCheerForTeam={showCheerForTeam}
            showBettingHistory={true}
            isFocus={editorIsFocus}
            beforePlaceholder={beforePlaceHolder}
            placeholder={`스포츠톡을 입력해보세요.\n*도배, 무의미한 글은 제약이 발생할 수 있습니다.`}
            callbackOpen={onOpen}
            callbackClose={null}
            callbackSave={onSave}
            minLength={0}
            maxLength={200}
            maxLine={100}
            fromViewType={editorFromViewType}
            editorData={editorData}
          />
        </AdvComments>
      </Fragment>
    )
  }

  const renderRow = (
    item: Schema$SportTalkListItem,
  ): React.ReactElement => {
    const { sportTalkItemType } = item

    if (sportTalkItemType === SPORT_TALK_ITEM_TYPE.CHEER_FOR_TEAM) {
      return (
        <SportTalkItem
          sportTalk={item.cheerForTeam}
          fetchCheerForTeams={() => {
            fetchSportTalks(track.matchId, true).catch(console.error)
          }}
        />
      )
    } else if (sportTalkItemType === SPORT_TALK_ITEM_TYPE.MATCH_TIMELINE) {
      return (
        <AdvTimelineItem advTimeline={item.matchTimeline}
                         simpleMatchTrack={track}/>
      )
    }

    return <Fragment/>
  }

  const renderFooter = () => {
    let footer
    if (_.size(sportTalkItems) > 0) {
      footer = <Fragment/>
    } else {
      footer = <AdvEmpty ico={EMPTY_ICON.SPORT_TALK_LIST}/>
    }
    return <div css={{ textAlign: 'center' }}>{footer}</div>
  }

  return (
    <Fragment>
      {
        _.isNil(track?.matchId) ?
          <AdvEmpty ico={EMPTY_ICON.MATCHUP_DETAIL_MATCH}/>
          :
          <ListView
            css={ListViewStyle}
            dataSource={dataSource.cloneWithRows(sportTalkItems ?? [])}
            renderHeader={renderHeader}
            renderRow={renderRow}
            renderFooter={renderFooter}
            initialListSize={_.size(sportTalkItems)}
            pageSize={_.size(sportTalkItems)}
          />
      }
    </Fragment>
  )
})
